import { UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import type { StoreProfileFormData } from '@/lib/validators';

interface StoreSizesFormProps {
  watch: UseFormWatch<StoreProfileFormData>;
  setValue: UseFormSetValue<StoreProfileFormData>;
}

const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SHOE_SIZES = [36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46];

export function StoreSizesForm({ watch, setValue }: StoreSizesFormProps) {
  const availableSizes = watch('availableSizes') || { tops: [], bottoms: [], shoes: [] };

  const toggleClothingSize = (type: 'tops' | 'bottoms', size: string) => {
    const current = availableSizes[type] || [];
    if (current.includes(size)) {
      setValue(`availableSizes.${type}`, current.filter((s) => s !== size));
    } else {
      setValue(`availableSizes.${type}`, [...current, size]);
    }
  };

  const toggleShoeSize = (size: number) => {
    const current = availableSizes.shoes || [];
    if (current.includes(size)) {
      setValue(
        'availableSizes.shoes',
        current.filter((s) => s !== size)
      );
    } else {
      setValue('availableSizes.shoes', [...current, size]);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Available Sizes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">Top Sizes</label>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            {CLOTHING_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => toggleClothingSize('tops', size)}
                className={`rounded-md border-2 px-4 py-2 text-sm font-medium transition-colors ${
                  availableSizes.tops?.includes(size)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">Bottom Sizes</label>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            {CLOTHING_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => toggleClothingSize('bottoms', size)}
                className={`rounded-md border-2 px-4 py-2 text-sm font-medium transition-colors ${
                  availableSizes.bottoms?.includes(size)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-3 block text-sm font-medium text-gray-700">Shoe Sizes (EU)</label>
          <div className="grid grid-cols-4 gap-2 md:grid-cols-6 lg:grid-cols-11">
            {SHOE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => toggleShoeSize(size)}
                className={`rounded-md border-2 px-3 py-2 text-sm font-medium transition-colors ${
                  availableSizes.shoes?.includes(size)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Select the sizes available in your store. This helps match deals to shoppers.
        </p>
      </CardContent>
    </Card>
  );
}
